// src/components/classes/AddClass.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  X,
  Calendar,
  Clock,
  Users,
  DollarSign,
  MapPin,
  BookOpen,
  User,
  Tag,
  FileText,
  AlertCircle,
  FolderOpen,
  Book,
  CheckCircle
} from 'lucide-react';
import { useClasses } from './ClassesContext';
import { useProjects } from '../projects/ProjectProvider';
import DatePicker from '../scheduling/DatePicker';
import classSessionsService from '../../services/classSessionsService';

// Move these components outside to prevent re-creation on every render
const InputField = ({ label, name, type = 'text', required = false, placeholder, icon: Icon, value, onChange, errors, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
      {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent ${
          errors[name] ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
        } bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`}
        {...props}
      />
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[name]}</p>
    )}
  </div>
);

const SelectField = ({ label, name, options, required = false, value, onChange, errors }) => (
  <div>
    <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
      {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent ${
        errors[name] ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
      } bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
    >
      {options.map(option => (
        <option key={option.value} value={option.value} className="bg-white dark:bg-gray-800">
          {option.label}
        </option>
      ))}
    </select>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[name]}</p>
    )}
  </div>
);

const TextAreaField = ({ label, name, required = false, placeholder, rows = 3, value, onChange, errors }) => (
  <div>
    <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
      {label} {required && <span className="text-red-500 dark:text-red-400">*</span>}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent ${
        errors[name] ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
      } bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`}
    />
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[name]}</p>
    )}
  </div>
);

const AddClass = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get class ID from URL for edit mode
  const { addClass, updateClass, getClass, loading, classes } = useClasses();
  const { projects } = useProjects();
  
  // Validate the ID parameter - if it's invalid, redirect back to catalog
  React.useEffect(() => {
    if (id && (id === 'NaN' || id === 'undefined' || id === 'null')) {
      console.error('AddClass - Invalid ID parameter received:', id);
      setSubmitError('Error: Invalid class ID. Redirecting to class catalog.');
      setTimeout(() => {
        window.location.href = '/classes';
      }, 2000);
      return;
    }
  }, [id]);
  
  const isEditMode = Boolean(id) && id !== 'NaN' && id !== 'undefined' && id !== 'null';
  
  
  
  const [formData, setFormData] = useState({
    title: '',
    instructor: '',
    category: '',
    skillLevel: 'Beginner',
    maxCapacity: '',
    location: '',
    price: '',
    materialsFee: '',
    materialsDescription: '',
    description: '',
    tags: [],
    specialInstructions: '',
    minimumAge: '',
    prerequisiteClassIds: [],
    projectId: '', // This is the key field that connects to backend
    waitlistEnabled: false, // New waitlist toggle
    followupClassIds: [], // Follow-up classes for student retention
    followupDescription: '' // Description for follow-up classes
  });

  // New session-based scheduling state
  const [selectedDates, setSelectedDates] = useState([]);
  const [timeSlot, setTimeSlot] = useState({ start: '10:00', duration: '' });
  const [conflictDates, setConflictDates] = useState([]);
  const [existingSessions, setExistingSessions] = useState([]);
  const [repeatOption, setRepeatOption] = useState('none'); // 'none', 'weekly', 'monthly'
  const [repeatConfig, setRepeatConfig] = useState({
    startDate: '',
    endDate: ''
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [currentTag, setCurrentTag] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Helper function to check if required fields in Basic Information tab are filled
  const areRequiredFieldsFilled = () => {
    const hasTitle = formData.title && formData.title.trim() !== '';
    const hasSkillLevel = formData.skillLevel && formData.skillLevel !== '';
    const hasMinAge = formData.minimumAge !== '' && formData.minimumAge !== null && formData.minimumAge !== undefined;

    console.log('Validation check:', { hasTitle, hasSkillLevel, hasMinAge, formData });
    return hasTitle && hasSkillLevel && hasMinAge;
  };

  // Helper function to check if pricing fields are filled
  const arePricingFieldsFilled = () => {
    const hasPrice = formData.price !== '' && formData.price !== null && formData.price !== undefined;
    return hasPrice;
  };

  // Helper function to check if two time slots overlap
  const hasTimeOverlap = (start1, duration1, start2, duration2) => {
    // Convert time strings to minutes since midnight
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const start1Minutes = timeToMinutes(start1);
    const end1Minutes = start1Minutes + duration1;
    const start2Minutes = timeToMinutes(start2);
    const end2Minutes = start2Minutes + duration2;

    // Check for overlap: sessions overlap if one starts before the other ends
    return start1Minutes < end2Minutes && start2Minutes < end1Minutes;
  };

  // Clear errors on component mount to prevent stale validation errors
  useEffect(() => {
    setErrors({});
    setSubmitError('');
    // Load existing sessions to show as info markers
    loadAllExistingSessions();
  }, []);


  // Load existing class data and sessions in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const loadClassAndSessions = async () => {
        try {
          // Load class data
          const classData = await getClass(id);
          if (classData) {
            setFormData({
              title: classData.title || '',
              instructor: classData.instructor || '',
              category: classData.category || '',
              skillLevel: classData.skillLevel || 'Beginner',
              maxCapacity: classData.maxCapacity || 20,
              location: classData.location || '',
              price: classData.price || 0,
              materialsFee: classData.materialsFee || 0,
              materialsDescription: classData.materialsDescription || '',
              description: classData.description || '',
              tags: classData.tags || [],
              specialInstructions: classData.specialInstructions || '',
              minimumAge: classData.minimumAge || '',
              prerequisiteClassIds: classData.prerequisiteClassIds || [],
              projectId: classData.projectId || '',
              waitlistEnabled: classData.waitlistEnabled === true,
              followupClassIds: classData.followupClassIds || [],
              followupDescription: classData.followupDescription || ''
            });

            // Load existing sessions
            const sessionsResult = await classSessionsService.getSessionsForClass(id);
            if (sessionsResult.success && sessionsResult.data.length > 0) {
              const existingSessions = classSessionsService.formatSessionsForDatePicker(sessionsResult.data);
              setSelectedDates(existingSessions);
              
              // Set time slot from first session
              const firstSession = sessionsResult.data[0];
              setTimeSlot({
                start: firstSession.start_time,
                duration: firstSession.duration
              });
            }
          }
        } catch (error) {
          console.error('Failed to load class:', error);
          setSubmitError('Failed to load class data. Please try again.');
        }
      };
      loadClassAndSessions();
    }
  }, [isEditMode, id, getClass]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value) || '') : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle dates change from DatePicker
  const handleDatesChange = (newDates) => {
    setSelectedDates(newDates);
    // Clear scheduling errors when dates are updated
    setErrors(prev => {
      const { sessions, duration, ...remainingErrors } = prev;
      return remainingErrors;
    });
  };

  // Handle time slot change from DatePicker
  const handleTimeSlotChange = (newTimeSlot) => {
    setTimeSlot(newTimeSlot);
    // Clear scheduling errors when time slot is updated
    setErrors(prev => {
      const { sessions, duration, ...remainingErrors } = prev;
      return remainingErrors;
    });
  };

  // Generate repeated dates based on repeat option
  const generateRepeatedDates = () => {
    if (repeatOption === 'none' || !repeatConfig.startDate || !repeatConfig.endDate) {
      return;
    }

    const startDate = new Date(repeatConfig.startDate + 'T12:00:00');
    const endDate = new Date(repeatConfig.endDate + 'T12:00:00');
    const dates = [];

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      dates.push(dateStr);

      // Increment based on repeat option
      if (repeatOption === 'weekly') {
        currentDate.setDate(currentDate.getDate() + 7);
      } else if (repeatOption === 'monthly') {
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }

    setSelectedDates(dates);
  };

  // Handle tag addition
  const addTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  // Handle tag removal
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle tag input key press
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Load all existing sessions to show as info markers
  const loadAllExistingSessions = async () => {
    try {
      const allSessionsResult = await classSessionsService.getUserSessions();
      
      if (allSessionsResult.success && allSessionsResult.data.length > 0) {
        // Filter out sessions from the current class if we're editing
        const otherClassSessions = allSessionsResult.data.filter(session => 
          !isEditMode || session.class_id !== id
        );
        
        // Convert to session info for display
        const sessionInfo = otherClassSessions.map(session => {
          // Use session_date directly to avoid timezone issues
          const dateStr = session.session_date;
          
          // Format time for display
          const formatTime = (timeStr) => {
            const [hours, minutes] = timeStr.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'pm' : 'am';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${displayHour}${minutes === '00' ? '' : ':' + minutes}${ampm}`;
          };
          
          const duration = session.duration || 60;
          const startMinutes = parseInt(session.start_time.split(':')[0]) * 60 + parseInt(session.start_time.split(':')[1]);
          const endMinutes = startMinutes + duration;
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          const endTimeStr = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
          
          const startTime = formatTime(session.start_time);
          const endTime = formatTime(endTimeStr);
          
          return {
            date: dateStr,
            startTime: session.start_time,
            duration: duration,
            displayText: `${session.class_title || 'Class'}: ${startTime}-${endTime}`,
            timeDisplay: `${startTime}-${endTime}`,
            classTitle: session.class_title || 'Unknown Class'
          };
        });
        
        setExistingSessions(sessionInfo);
      }
    } catch (error) {
      console.error('Failed to load existing sessions:', error);
    }
  };

  // Check for scheduling conflicts
  const checkConflicts = async () => {
    if (selectedDates.length === 0) return;

    const sessionsToCheck = classSessionsService.formatDatesForAPI(selectedDates, timeSlot.duration || 60);
    const conflictResult = await classSessionsService.checkConflicts(
      isEditMode ? id : null, 
      sessionsToCheck
    );

    if (conflictResult.success && conflictResult.hasConflicts) {
      const conflictDateStrings = conflictResult.conflicts.map(c => c.date);
      setConflictDates(conflictDateStrings);
      return true;
    } else {
      setConflictDates([]);
      return false;
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required: Class Title
    if (!formData.title.trim()) {
      newErrors.title = 'Class title is required';
    }

    // Required: Skill Level
    if (!formData.skillLevel || !formData.skillLevel.trim()) {
      newErrors.skillLevel = 'Skill level is required';
    }

    // Required: Minimum Age
    const minimumAge = parseInt(formData.minimumAge);
    if (isNaN(minimumAge) || minimumAge < 0) {
      newErrors.minimumAge = 'Minimum age is required and must be 0 or greater';
    }

    // Required: Price
    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      newErrors.price = 'Price is required and cannot be negative';
    }

    // Required: Session validation
    if (selectedDates.length === 0) {
      newErrors.sessions = 'At least one class session is required';
    }

    const duration = parseInt(timeSlot.duration) || 0;
    if (duration === 0) {
      newErrors.duration = 'Duration is required';
    } else if (duration < 15) {
      newErrors.duration = 'Duration must be at least 15 minutes';
    }

    // Optional: Max Capacity (only validate if provided)
    if (formData.maxCapacity) {
      const maxCapacity = parseFloat(formData.maxCapacity);
      if (maxCapacity < 1) {
        newErrors.maxCapacity = 'Max capacity must be at least 1';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update class sessions in edit mode
  const updateClassSessions = async (classId) => {
    try {
      // Get current sessions from the database
      const currentSessionsResult = await classSessionsService.getSessionsForClass(classId);
      if (!currentSessionsResult.success) {
        throw new Error('Failed to load current sessions');
      }
      
      const currentSessions = currentSessionsResult.data || [];
      const newSessions = classSessionsService.formatDatesForAPI(selectedDates, timeSlot.duration || 60);
      
      
      // Create a map of current sessions by date for easy lookup
      const currentSessionMap = new Map();
      currentSessions.forEach(session => {
        const dateStr = session.session_date; // Should be in YYYY-MM-DD format
        currentSessionMap.set(dateStr, session);
      });
      
      // Create a map of new sessions by date
      const newSessionMap = new Map();
      newSessions.forEach(session => {
        newSessionMap.set(session.session_date, session);
      });
      
      // Find sessions to delete (in current but not in new)
      const sessionsToDelete = [];
      for (const [dateStr, session] of currentSessionMap) {
        if (!newSessionMap.has(dateStr)) {
          sessionsToDelete.push(session);
        }
      }
      
      // Find sessions to create (in new but not in current)
      const sessionsToCreate = [];
      for (const [dateStr, session] of newSessionMap) {
        if (!currentSessionMap.has(dateStr)) {
          sessionsToCreate.push(session);
        }
      }
      
      // Find sessions to update (in both but with different time/duration)
      const sessionsToUpdate = [];
      for (const [dateStr, newSession] of newSessionMap) {
        const currentSession = currentSessionMap.get(dateStr);
        if (currentSession) {
          // Check if time or duration changed
          if (currentSession.start_time !== newSession.start_time || 
              currentSession.duration !== newSession.duration) {
            sessionsToUpdate.push({
              id: currentSession.id,
              updates: {
                start_time: newSession.start_time,
                duration: newSession.duration
              }
            });
          }
        }
      }
      
      
      // Delete removed sessions
      for (const session of sessionsToDelete) {
        const deleteResult = await classSessionsService.deleteSession(session.id);
        if (!deleteResult.success) {
          console.error('Failed to delete session:', session.id, deleteResult.error);
        }
      }
      
      // Create new sessions
      if (sessionsToCreate.length > 0) {
        const createResult = await classSessionsService.createSessions(classId, sessionsToCreate);
        if (!createResult.success) {
          console.error('Failed to create sessions:', createResult.error);
          throw new Error(`Failed to create new sessions: ${createResult.error}`);
        }
      }
      
      // Update existing sessions
      for (const sessionUpdate of sessionsToUpdate) {
        const updateResult = await classSessionsService.updateSession(sessionUpdate.id, sessionUpdate.updates);
        if (!updateResult.success) {
          console.error('Failed to update session:', sessionUpdate.id, updateResult.error);
        }
      }
      
      
    } catch (error) {
      console.error('Failed to update class sessions:', error);
      throw new Error(`Failed to update class sessions: ${error.message}`);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    // Check for conflicts before saving
    const hasConflicts = await checkConflicts();
    if (hasConflicts) {
      setSubmitError('Please resolve scheduling conflicts before saving.');
      return;
    }

    try {
      let classId;
      
      if (isEditMode) {
        // Update existing class - include duration from timeSlot
        const classDataWithDuration = {
          ...formData,
          duration: parseInt(timeSlot.duration) || 60
        };
        await updateClass(id, classDataWithDuration);
        classId = id;
        
        // Update existing sessions
        await updateClassSessions(classId);
      } else {
        // Create new class - include duration from timeSlot
        const classDataWithDuration = {
          ...formData,
          duration: parseInt(timeSlot.duration) || 60
        };
        const classResult = await addClass(classDataWithDuration);
        classId = classResult.id; // Use the ID from the created class
      }

      // Create sessions (only for new classes, edit mode handles sessions separately)
      if (!isEditMode && selectedDates.length > 0 && classId) {
        
        // Add a small delay to ensure class creation is fully committed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const sessionsToCreate = classSessionsService.formatDatesForAPI(selectedDates, timeSlot.duration || 60);
        const sessionResult = await classSessionsService.createSessions(classId, sessionsToCreate);
        
        if (!sessionResult.success) {
          console.error('Sessions creation failed:', sessionResult.error);
          console.error('Class ID used:', classId);
          console.error('Sessions data sent:', sessionsToCreate);
          // Don't fail the whole operation, but warn the user
          setSubmitError(`Class saved but some sessions failed to create: ${sessionResult.error}`);
          return;
        }
      }

      navigate('/classes');
    } catch (error) {
      console.error('Failed to save class:', error);
      setSubmitError(error.message || 'Failed to save class. Please try again.');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    navigate('/classes');
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/classes')}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-[#7A99D8] dark:hover:text-[#8FAEE6] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <BookOpen className="w-8 h-8 text-[#7A99D8] dark:text-[#8FAEE6]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-100">
                {isEditMode ? 'Edit Class' : 'Add New Class'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isEditMode ? 'Update class information' : 'Create a new class for student enrollment'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div>{/* Content wrapper */}

        {/* Error Message */}
        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error {isEditMode ? 'updating' : 'creating'} class</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{submitError}</p>
            </div>
            <button
              onClick={() => setSubmitError('')}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === 'details'
                  ? 'text-[#7A99D8] dark:text-blue-400 bg-[#F5F5F5] dark:bg-gray-700'
                  : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span>Basic Information</span>
              </div>
              {activeTab === 'details' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A99D8]"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => areRequiredFieldsFilled() && setActiveTab('schedule')}
              disabled={!areRequiredFieldsFilled()}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === 'schedule'
                  ? 'text-[#7A99D8] dark:text-blue-400 bg-[#F5F5F5] dark:bg-gray-700'
                  : !areRequiredFieldsFilled()
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Scheduling</span>
                {selectedDates.length > 0 && (
                  <CheckCircle className="w-4 h-4 text-[#7A99D8]" />
                )}
              </div>
              {activeTab === 'schedule' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A99D8]"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => areRequiredFieldsFilled() && setActiveTab('pricing')}
              disabled={!areRequiredFieldsFilled()}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
                activeTab === 'pricing'
                  ? 'text-[#7A99D8] dark:text-blue-400 bg-[#F5F5F5] dark:bg-gray-700'
                  : !areRequiredFieldsFilled()
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-600 dark:text-gray-400 hover:text-[#3C3C3C] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <DollarSign className="w-4 h-4" />
                <span>Pricing</span>
              </div>
              {activeTab === 'pricing' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7A99D8]"></div>
              )}
            </button>
          </div>
        </div>

        {/* Form */}
        <form id="class-form" onSubmit={handleSubmit} className="space-y-8">
          {/* Tab 1: Basic Information */}
          {activeTab === 'details' && (
          <>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-6">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Class Title"
                name="title"
                required
                placeholder="e.g., Beginning Sewing, Pattern Making"
                icon={BookOpen}
                value={formData.title}
                onChange={handleChange}
                errors={errors}
              />

              <InputField
                label="Instructor"
                name="instructor"
                placeholder="e.g., Sarah Johnson"
                icon={User}
                value={formData.instructor}
                onChange={handleChange}
                errors={errors}
              />

              <InputField
                label="Category"
                name="category"
                placeholder="e.g., Garment Construction, Quilting, Embroidery"
                icon={Tag}
                value={formData.category}
                onChange={handleChange}
                errors={errors}
              />

              <SelectField
                label="Skill Level"
                name="skillLevel"
                required
                options={[
                  { value: 'Beginner', label: 'Beginner' },
                  { value: 'Intermediate', label: 'Intermediate' },
                  { value: 'Advanced', label: 'Advanced' }
                ]}
                value={formData.skillLevel}
                onChange={handleChange}
                errors={errors}
              />

              <InputField
                label="Minimum Age"
                name="minimumAge"
                type="number"
                required
                min="0"
                max="120"
                placeholder="e.g., 16"
                icon={User}
                value={formData.minimumAge}
                onChange={handleChange}
                errors={errors}
              />

              <InputField
                label="Max Capacity"
                name="maxCapacity"
                type="number"
                min="1"
                placeholder="e.g., 20"
                icon={Users}
                value={formData.maxCapacity}
                onChange={handleChange}
                errors={errors}
              />

              {/* Prerequisite Classes */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
                  Prerequisite Class
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Book className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <select
                    name="prerequisiteClassIds"
                    value={formData.prerequisiteClassIds[0] || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        prerequisiteClassIds: e.target.value ? [e.target.value] : []
                      }));
                    }}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent ${
                      errors.prerequisiteClassIds ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
                    } bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                  >
                    <option value="" className="bg-white dark:bg-gray-800">No prerequisite required</option>
                    {classes
                      .filter(cls => cls.id !== id) // Don't show current class in edit mode
                      .map(cls => (
                        <option key={cls.id} value={cls.id} className="bg-white dark:bg-gray-800">
                          {cls.title} ({cls.skillLevel})
                        </option>
                      ))
                    }
                  </select>
                </div>
                {errors.prerequisiteClassIds && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.prerequisiteClassIds}</p>
                )}
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Students must complete this class before enrolling
                </p>
              </div>

              {/* Project Assignment */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
                  Attach Project
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FolderOpen className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent ${
                      errors.projectId ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
                    } bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                  >
                    <option value="" className="bg-white dark:bg-gray-800">Select a project (optional)...</option>
                    {projects && projects.map(project => (
                      <option key={project.id} value={project.id} className="bg-white dark:bg-gray-800">
                        {project.name || project.title}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.projectId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.projectId}</p>
                )}
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Select the project that students will work on during this class
                </p>
              </div>
            </div>
          </div>

          {/* Follow-up Classes Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mt-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">Follow-up Classes</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Help retain students by suggesting classes they should take after completing this one. These will be shown during the enrollment process.
            </p>

            <div className="space-y-6">
              {/* Follow-up Classes Multi-Select */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
                  Suggested Follow-up Classes
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BookOpen className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      const classId = e.target.value;
                      const currentFollowupIds = formData.followupClassIds || [];
                      if (classId && !currentFollowupIds.includes(classId)) {
                        setFormData(prev => ({
                          ...prev,
                          followupClassIds: [...(prev.followupClassIds || []), classId]
                        }));
                      }
                      e.target.value = '';
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Add a follow-up class...</option>
                    {classes
                      .filter(cls => cls.id !== id && !(formData.followupClassIds || []).includes(cls.id))
                      .map(cls => {
                        // Format day, time, and dates for dropdown
                        let scheduleInfo = '';
                        const parts = [];

                        // Add day of week
                        if (cls.dayOfWeek) parts.push(cls.dayOfWeek + 's');

                        // Add time
                        if (cls.startTime) {
                          const time = new Date(`2000-01-01T${cls.startTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                          parts.push(time);
                        }

                        // Add date range
                        const firstDate = cls.firstSessionDate || cls.startDate;
                        const lastDate = cls.lastSessionDate || cls.endDate;
                        if (firstDate && lastDate) {
                          const first = new Date(firstDate);
                          const last = new Date(lastDate);
                          const dateRange = `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                          parts.push(dateRange);
                        } else if (firstDate) {
                          parts.push(new Date(firstDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
                        }

                        scheduleInfo = parts.length > 0 ? ` - ${parts.join(', ')}` : '';

                        return (
                          <option key={cls.id} value={cls.id} className="bg-white dark:bg-gray-800">
                            {cls.title} ({cls.skillLevel}){scheduleInfo}
                          </option>
                        );
                      })
                    }
                  </select>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Select classes that students should consider taking next
                </p>

                {/* Selected Follow-up Classes */}
                {formData.followupClassIds && formData.followupClassIds.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {formData.followupClassIds.map(classId => {
                      const followupClass = classes.find(c => c.id === classId);
                      if (!followupClass) return null;
                      return (
                        <div
                          key={classId}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex items-center gap-3">
                            <BookOpen className="w-4 h-4 text-[#7A99D8]" />
                            <div className="flex-1">
                              <div className="font-medium text-sm text-[#3C3C3C] dark:text-gray-200">
                                {followupClass.title}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                {followupClass.skillLevel} • {followupClass.instructor || 'No instructor'}
                              </div>
                              {/* Session dates and times */}
                              {(followupClass.dayOfWeek || followupClass.startTime || followupClass.firstSessionDate || followupClass.startDate) && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {/* Day and Time */}
                                  {(followupClass.dayOfWeek || followupClass.startTime) && (
                                    <div>
                                      {followupClass.dayOfWeek && <span>{followupClass.dayOfWeek}s</span>}
                                      {followupClass.startTime && (
                                        <span>
                                          {followupClass.dayOfWeek && ' • '}
                                          {new Date(`2000-01-01T${followupClass.startTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {/* Date Range - prefer session dates over class dates */}
                                  {(() => {
                                    const firstDate = followupClass.firstSessionDate || followupClass.startDate;
                                    const lastDate = followupClass.lastSessionDate || followupClass.endDate;
                                    if (firstDate) {
                                      const formatDate = (dateStr) => {
                                        const date = new Date(dateStr);
                                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                      };
                                      return (
                                        <div>
                                          {formatDate(firstDate)}
                                          {lastDate && ` - ${formatDate(lastDate)}`}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                followupClassIds: prev.followupClassIds.filter(id => id !== classId)
                              }));
                            }}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Follow-up Description */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-1">
                  Follow-up Description
                </label>
                <textarea
                  name="followupDescription"
                  value={formData.followupDescription}
                  onChange={handleChange}
                  rows={3}
                  placeholder="e.g., 'After mastering the basics in this class, students are ready to advance their skills with these intermediate-level courses...'"
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-vertical"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Explain to students why they should consider these classes next. This will be shown during enrollment.
                </p>
              </div>
            </div>
          </div>

          {/* Spacer for sticky buttons */}
          <div className="h-20"></div>
          </>
          )}

          {/* Tab 2: Scheduling */}
          {activeTab === 'schedule' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-6">
              Class Scheduling <span className="text-red-500 dark:text-red-400">*</span>
            </h2>
            {errors.sessions && errors.sessions.trim() && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">{errors.sessions}</p>
              </div>
            )}

            {/* Repeat Options */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-3">
                Repeat Schedule
              </label>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Repeat Type */}
                <div>
                  <select
                    value={repeatOption}
                    onChange={(e) => setRepeatOption(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="none">No Repeat</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {/* Date Range */}
                {repeatOption !== 'none' && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={repeatConfig.startDate}
                        onChange={(e) => setRepeatConfig(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                      <input
                        type="date"
                        value={repeatConfig.endDate}
                        onChange={(e) => setRepeatConfig(prev => ({ ...prev, endDate: e.target.value }))}
                        min={repeatConfig.startDate}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>

                    {/* Generate Button */}
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={generateRepeatedDates}
                        disabled={!repeatConfig.startDate || !repeatConfig.endDate}
                        className="w-full px-4 py-2 bg-[#7A99D8] hover:bg-[#6A89C8] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors text-sm"
                      >
                        Generate
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div onFocus={() => setErrors(prev => {
              const { sessions, duration, ...rest } = prev;
              return rest;
            })}>
              <DatePicker
                selectedDates={selectedDates}
                onDatesChange={handleDatesChange}
                timeSlot={timeSlot}
                onTimeSlotChange={handleTimeSlotChange}
                conflictDates={conflictDates}
                existingSessions={existingSessions}
              />
            </div>

            {/* Spacer for sticky buttons */}
            <div className="h-20"></div>
          </div>
          )}

          {/* Tab 3: Pricing */}
          {activeTab === 'pricing' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-6">Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Price ($)"
                name="price"
                type="number"
                required
                min="0"
                step="0.01"
                placeholder="e.g., 150.00"
                icon={DollarSign}
                value={formData.price}
                onChange={handleChange}
                errors={errors}
              />

              <InputField
                label="Materials Fee ($)"
                name="materialsFee"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g., 25.00"
                icon={DollarSign}
                value={formData.materialsFee}
                onChange={handleChange}
                errors={errors}
              />

              <InputField
                label="Location"
                name="location"
                placeholder="e.g., Room 101, Online"
                icon={MapPin}
                value={formData.location}
                onChange={handleChange}
                errors={errors}
              />

              {/* Waitlist Toggle */}
              <div className="flex items-center space-x-3 pt-6">
                <input
                  type="checkbox"
                  id="waitlistEnabled"
                  name="waitlistEnabled"
                  checked={formData.waitlistEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, waitlistEnabled: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="waitlistEnabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Enable Waitlist when full
                </label>
              </div>
            </div>

            {/* Total Price Display */}
            {(formData.price > 0 || formData.materialsFee > 0) && (
              <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Total Class Price:</span>
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    ${(parseFloat(formData.price || 0) + parseFloat(formData.materialsFee || 0)).toFixed(2)}
                  </span>
                </div>
                {formData.materialsFee > 0 && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    Base: ${parseFloat(formData.price || 0).toFixed(2)} + Materials: ${parseFloat(formData.materialsFee || 0).toFixed(2)}
                  </div>
                )}
              </div>
            )}

            {/* Spacer for sticky buttons */}
            <div className="h-20"></div>
          </div>
          )}
        </form>

        {/* Sticky Navigation Buttons - Basic Information Tab */}
        {activeTab === 'details' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-3 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => areRequiredFieldsFilled() && setActiveTab('schedule')}
              disabled={!areRequiredFieldsFilled()}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium ${
                areRequiredFieldsFilled()
                  ? 'bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C]'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              Next: Scheduling
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        )}

        {/* Sticky Navigation Buttons - Schedule Tab */}
        {activeTab === 'schedule' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className="px-6 py-3 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back: Basic Information
            </button>
            <button
              type="button"
              onClick={() => areRequiredFieldsFilled() && setActiveTab('pricing')}
              disabled={!areRequiredFieldsFilled()}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium ${
                areRequiredFieldsFilled()
                  ? 'bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C]'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              Next: Pricing
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
        )}

        {/* Sticky Navigation Buttons - Pricing Tab */}
        {activeTab === 'pricing' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => setActiveTab('schedule')}
              className="px-6 py-3 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back: Scheduling
            </button>
            <button
              type="submit"
              form="class-form"
              disabled={loading || !arePricingFieldsFilled()}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium border border-transparent ${
                loading || !arePricingFieldsFilled()
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-[#FFECC5] dark:bg-[#FFE4A3]/20 hover:bg-[#FFE4A3] dark:hover:bg-[#FFE4A3]/30 text-[#3C3C3C] dark:text-gray-200 dark:border-[#FFE4A3]/40'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#3C3C3C] dark:border-gray-200"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Class' : 'Create Class'}
                </>
              )}
            </button>
          </div>
        </div>
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-amber-500 dark:text-amber-400 mr-3" />
                  <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">Cancel Changes</h3>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to cancel? Your changes will be lost.
                </p>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 rounded-lg font-medium transition-colors"
                  >
                    Continue Editing
                  </button>
                  <button
                    onClick={confirmCancel}
                    className="px-4 py-2 bg-amber-600 dark:bg-amber-700 hover:bg-amber-700 dark:hover:bg-amber-800 text-white rounded-lg font-medium transition-colors"
                  >
                    Cancel & Lose Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AddClass;